import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert, Badge, Button, ButtonGroup, Col,
	Collapse, Input, Row, Spinner
} from 'reactstrap';
import handbookService from './HandbookService';
import { SectionSettingsPanel } from './HandbookSectionEditor';
import { ItemEditorPanel } from './HandbookItemEditor';
import {
	FiEdit3, FiLink, FiFileText, FiImage, FiUser,
	FiCheckSquare, FiAlertTriangle, FiFilm, FiSave,
	FiClipboard, FiBookOpen, FiChevronUp, FiChevronDown, FiX,
	FiUserPlus
} from 'react-icons/fi';

function newGuid() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = Math.random() * 16 | 0;
		const v = c === 'x' ? r : (r & 0x3 | 0x8);
		return v.toString(16);
	});
}

function slugify(value, fallback) {
	const source = (value || fallback || '').toLowerCase().trim();
	const normalized = source.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
	return normalized || fallback || 'section';
}

function createSection(template, existingCount) {
	const keyBase = template && template.key ? template.key : `section-${existingCount + 1}`;
	return {
		id: newGuid(),
		key: keyBase,
		name: template && template.name ? template.name : 'New Section',
		name_cy: template && template.name_cy ? template.name_cy : '',
		description: template && template.description ? template.description : '',
		description_cy: template && template.description_cy ? template.description_cy : '',
		displayOrder: existingCount + 1,
		enabled: true,
		items: []
	};
}

function createPerson() {
	return { id: newGuid(), name: '', role: '', role_cy: '', email: '', phone: '', address: '', imageUrl: '' };
}

function createLinkEntry() {
	return { id: newGuid(), name: '', name_cy: '', url: '', url_cy: '', logoImageUrl: '', htmlContent: '', htmlContent_cy: '' };
}

function createPdfEntry() {
	return { id: newGuid(), name: '', name_cy: '', url: '', url_cy: '', htmlContent: '', htmlContent_cy: '' };
}

function createItem(sectionId, existingCount) {
	return {
		id: newGuid(),
		sectionId,
		title: '',
		title_cy: '',
		content: '',
		content_cy: '',
		itemType: 'TextBlock',
		subsectionType: 'Guidance',
		isCollapsible: false,
		links: [],
		pdfs: [],
		people: [],
		displayOrder: existingCount + 1,
		enabled: true
	};
}

function createPersonCard(sectionId, existingCount) {
	return {
		id: newGuid(),
		sectionId,
		title: '',
		title_cy: '',
		content: '',
		content_cy: '',
		itemType: 'PersonCard',
		subsectionType: 'Contacts',
		isCollapsible: false,
		links: [],
		pdfs: [],
		people: [createPerson()],
		displayOrder: existingCount + 1,
		enabled: true
	};
}

function getEmbedVideoUrl(url) {
	if (!url) return '';
	try {
		if (url.includes('youtube.com/watch?v=')) return `https://www.youtube.com/embed/${url.split('v=')[1].split('&')[0]}`;
		if (url.includes('youtu.be/')) return `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split('?')[0]}`;
		if (url.includes('vimeo.com/')) return `https://player.vimeo.com/video/${url.split('vimeo.com/')[1].split('?')[0]}`;
	} catch (e) { return ''; }
	return '';
}

function ItemMiniPreview({ item }) {
	if (!item) return null;

	if (item.itemType === 'PersonCard') {
		const people = item.people && item.people.length > 0 ? item.people : [];
		if (people.length === 0) {
			return <div className="hb-admin-preview-card text-muted" style={{ fontSize: '0.85em' }}>No people added yet.</div>;
		}
		return (
			<div>
				{people.map(p => (
					<div key={p.id} className="hb-admin-preview-card hb-admin-preview-person mb-2">
						{p.imageUrl && <img src={p.imageUrl} alt={p.name} className="hb-admin-preview-avatar" />}
						<div>
							<strong>{p.name || '(no name)'}</strong>
							{(p.role || p.role_cy) && <div className="text-muted" style={{ fontSize: '0.85em' }}>{p.role || p.role_cy}</div>}
							{p.email && <div style={{ fontSize: '0.85em' }}><a href={`mailto:${p.email}`}>{p.email}</a></div>}
							{p.phone && <div style={{ fontSize: '0.85em' }}>{p.phone}</div>}
						</div>
					</div>
				))}
			</div>
		);
	}

	if (item.itemType === 'Link') {
		const links = item.links && item.links.length > 0 ? item.links : [];
		return (
			<div className="hb-admin-preview-card">
				<strong>{item.title || 'Links'}</strong>
				{links.length === 0 ? <div className="text-muted" style={{ fontSize: '0.85em' }}>No links added yet.</div> : links.map(link => (
					<div key={link.id || link.url} style={{ marginTop: 6 }}>
						<div style={{ fontSize: '0.9em' }}><a href={link.url_cy || link.url || '#'} target="_blank" rel="noopener noreferrer">{link.name || link.name_cy || link.url || 'Untitled link'}</a></div>
						{link.htmlContent && <div style={{ fontSize: '0.8em' }} dangerouslySetInnerHTML={{ __html: link.htmlContent }} />}
					</div>
				))}
			</div>
		);
	}

	if (item.itemType === 'Pdf') {
		const pdfs = item.pdfs && item.pdfs.length > 0 ? item.pdfs : [];
		return (
			<div className="hb-admin-preview-card">
				<strong>{item.title || 'PDFs'}</strong>
				{pdfs.length === 0 ? <div className="text-muted" style={{ fontSize: '0.85em' }}>No PDFs added yet.</div> : pdfs.map(pdf => (
					<div key={pdf.id || pdf.url} style={{ marginTop: 6 }}>
						<div style={{ fontSize: '0.9em' }}><a href={pdf.url_cy || pdf.url || '#'} target="_blank" rel="noopener noreferrer">{pdf.name || pdf.name_cy || pdf.url || 'Untitled PDF'}</a></div>
						{pdf.htmlContent && <div style={{ fontSize: '0.8em' }} dangerouslySetInnerHTML={{ __html: pdf.htmlContent }} />}
					</div>
				))}
			</div>
		);
	}

	if (item.itemType === 'Video') {
		const embedUrl = getEmbedVideoUrl(item.videoUrl || item.linkUrl);
		return (
			<div className="hb-admin-preview-card">
				<strong>{item.title || 'Untitled video'}</strong>
				{item.content && <div style={{ fontSize: '0.85em' }} dangerouslySetInnerHTML={{ __html: item.content }} />}
				{embedUrl && (
					<div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginTop: '8px' }}>
						<iframe title={item.title || 'video'} src={embedUrl} frameBorder="0" allowFullScreen style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
					</div>
				)}
			</div>
		);
	}

	if (item.itemType === 'Callout') {
		return (
			<Alert color="warning" className="mb-0 py-2">
				<strong>{item.title || 'Callout'}</strong>
				{item.content && <div style={{ fontSize: '0.9em' }} dangerouslySetInnerHTML={{ __html: item.content }} />}
			</Alert>
		);
	}

	return (
		<div className="hb-admin-preview-card">
			<strong>{item.title || (item.title_cy ? item.title_cy : 'Untitled item')}</strong>
			{item.imageUrl && <img src={item.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: '150px', display: 'block', marginTop: '6px' }} />}
			{item.content && <div style={{ fontSize: '0.9em', marginTop: '4px' }} dangerouslySetInnerHTML={{ __html: item.content }} />}
			{item.linkUrl && <div style={{ fontSize: '0.85em' }}><a href={item.linkUrl} target="_blank" rel="noopener noreferrer">Link &rarr;</a></div>}
			{item.documentUrl && <div style={{ fontSize: '0.85em' }}><a href={item.documentUrl} target="_blank" rel="noopener noreferrer">Document &rarr;</a></div>}
		</div>
	);
}

function ItemTypeIcon({ itemType }) {
	const iconMap = {
		TextBlock: FiEdit3,
		Link: FiLink,
		Pdf: FiFileText,
		PersonCard: FiUser
	};
	const Icon = iconMap[itemType] || FiEdit3;
	return <span style={{ marginRight: '6px', display: 'inline-flex', alignItems: 'center' }}><Icon size={14} /></span>;
}

export function HandbookEditor() {
	const [document, setDocument] = useState({ sections: [] });
	const documentRef = useRef({ sections: [] });
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [selectedSectionId, setSelectedSectionId] = useState('');
	const [selectedItemId, setSelectedItemId] = useState('');
	const [sectionSettingsOpen, setSectionSettingsOpen] = useState(false);
	const [showPreview, setShowPreview] = useState(false);

	useEffect(() => { load(); }, []);

	function normalizeIncomingDocument(doc) {
    if (!doc || !doc.sections) return;
    (doc.sections || []).forEach(section => {
        (section.items || []).forEach(item => {
            item.people = item.people || [];
            item.links = item.links || [];
            item.pdfs = item.pdfs || [];

            // normalize people fields (roleCy -> role_cy)
            (item.people || []).forEach(p => {
                if (typeof p.role_cy === 'undefined' && typeof p.roleCy !== 'undefined') p.role_cy = p.roleCy;
                if (typeof p.name_cy === 'undefined' && typeof p.nameCy !== 'undefined') p.name_cy = p.nameCy;
                if (typeof p.imageUrl === 'undefined' && typeof p.image_url !== 'undefined') p.imageUrl = p.image_url;
            });

            // normalize links
            (item.links || []).forEach(l => {
                if (typeof l.name_cy === 'undefined' && typeof l.nameCy !== 'undefined') l.name_cy = l.nameCy;
                if (typeof l.url_cy === 'undefined' && typeof l.urlCy !== 'undefined') l.url_cy = l.urlCy;
                if (typeof l.htmlContent_cy === 'undefined' && typeof l.htmlContentCy !== 'undefined') l.htmlContent_cy = l.htmlContentCy;
            });

            // normalize pdfs
            (item.pdfs || []).forEach(p => {
                if (typeof p.name_cy === 'undefined' && typeof p.nameCy !== 'undefined') p.name_cy = p.nameCy;
                if (typeof p.url_cy === 'undefined' && typeof p.urlCy !== 'undefined') p.url_cy = p.urlCy;
                if (typeof p.htmlContent_cy === 'undefined' && typeof p.htmlContentCy !== 'undefined') p.htmlContent_cy = p.htmlContentCy;
            });
        });
    });
}

	const load = async (preserveSectionId, preserveItemId) => {
		setLoading(true);
		setError('');
		try {
			const [handbook, templateData] = await Promise.all([
				handbookService.getAdminDocument(),
				handbookService.getTemplateSections()
			]);
			const nextDocument = handbook || { sections: [] };
			normalizeIncomingDocument(nextDocument);
			documentRef.current = nextDocument;
			setDocument(nextDocument);
			setTemplates(templateData || []);

			const sections = nextDocument.sections || [];

			const targetSection = preserveSectionId
				? sections.find(s => s.id === preserveSectionId) || sections[0] || null
				: sections[0] || null;

			setSelectedSectionId(targetSection ? targetSection.id : '');

			const items = targetSection ? (targetSection.items || []) : [];
			const targetItem = preserveItemId
				? items.find(i => i.id === preserveItemId) || items[0] || null
				: items[0] || null;

			setSelectedItemId(targetItem ? targetItem.id : '');
		} catch (e) {
			setError('Could not load handbook editor.');
		} finally {
			setLoading(false);
		}
	};

	const selectedSection = useMemo(
		() => (document.sections || []).find(x => x.id === selectedSectionId) || document.sections[0] || null,
		[document, selectedSectionId]
	);

	const selectedItem = useMemo(
		() => (selectedSection && selectedSection.items ? selectedSection.items.find(x => x.id === selectedItemId) : null) || null,
		[selectedSection, selectedItemId]
	);

	const updateDocument = (updater) => {
		setSuccess('');
		setDocument(prev => {
			const next = updater(prev);
			documentRef.current = next;
			return next;
		});
	};

	const updateSection = (sectionId, updater) => {
		updateDocument(prev => ({
			...prev,
			sections: (prev.sections || []).map(s => s.id === sectionId ? updater(s) : s)
		}));
	};

	const updateItem = (sectionId, itemId, updater) => {
		updateSection(sectionId, section => ({
			...section,
			items: (section.items || []).map(item => item.id === itemId ? updater(item) : item)
		}));
	};

	const updatePerson = (personId, updater) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			people: (item.people || []).map(p => p.id === personId ? updater(p) : p)
		}));
	};

	const addPerson = () => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			people: [...(item.people || []), createPerson()]
		}));
	};

	const deletePerson = (personId) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			people: (item.people || []).filter(p => p.id !== personId)
		}));
	};

	const movePerson = (personId, direction) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => {
			const people = [...(item.people || [])];
			const idx = people.findIndex(p => p.id === personId);
			const swapIdx = idx + direction;
			if (idx < 0 || swapIdx < 0 || swapIdx >= people.length) return item;
			[people[idx], people[swapIdx]] = [people[swapIdx], people[idx]];
			return { ...item, people };
		});
	};

	const updateLink = (linkId, updater) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			links: (item.links || []).map(l => l.id === linkId ? updater(l) : l)
		}));
	};

	const addLink = () => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			links: [...(item.links || []), createLinkEntry()]
		}));
	};

	const deleteLink = (linkId) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			links: (item.links || []).filter(l => l.id !== linkId)
		}));
	};

	const moveLink = (linkId, direction) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => {
			const links = [...(item.links || [])];
			const idx = links.findIndex(l => l.id === linkId);
			const swapIdx = idx + direction;
			if (idx < 0 || swapIdx < 0 || swapIdx >= links.length) return item;
			[links[idx], links[swapIdx]] = [links[swapIdx], links[idx]];
			return { ...item, links };
		});
	};

	const updatePdf = (pdfId, updater) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			pdfs: (item.pdfs || []).map(p => p.id === pdfId ? updater(p) : p)
		}));
	};

	const addPdf = () => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			pdfs: [...(item.pdfs || []), createPdfEntry()]
		}));
	};

	const deletePdf = (pdfId) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			pdfs: (item.pdfs || []).filter(p => p.id !== pdfId)
		}));
	};

	const movePdf = (pdfId, direction) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => {
			const pdfs = [...(item.pdfs || [])];
			const idx = pdfs.findIndex(p => p.id === pdfId);
			const swapIdx = idx + direction;
			if (idx < 0 || swapIdx < 0 || swapIdx >= pdfs.length) return item;
			[pdfs[idx], pdfs[swapIdx]] = [pdfs[swapIdx], pdfs[idx]];
			return { ...item, pdfs };
		});
	};

	const handleSectionChange = (name, nextValue) => {
		if (!selectedSection) return;
		updateSection(selectedSection.id, section => ({
			...section,
			[name]: name === 'displayOrder' ? Number(nextValue) : nextValue,
			key: name === 'name' && (!section.key || section.key.startsWith('section-')) ? slugify(nextValue, section.key) : section.key
		}));
	};

	const handleItemChange = (name, nextValue) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, item => ({
			...item,
			[name]: name === 'displayOrder' ? Number(nextValue) : nextValue
		}));
	};

	const handleUpdateItem = (updater) => {
		if (!selectedSection || !selectedItem) return;
		updateItem(selectedSection.id, selectedItem.id, updater);
	};

	const addSection = (template) => {
		updateDocument(prev => {
			const nextSection = createSection(template, (prev.sections || []).length);
			setSelectedSectionId(nextSection.id);
			setSelectedItemId('');
			return { ...prev, sections: [...(prev.sections || []), nextSection] };
		});
	};

	const deleteSection = (sectionId) => {
		if (!window.confirm('Delete this section and all its items?')) return;
		updateDocument(prev => {
			const nextSections = (prev.sections || []).filter(x => x.id !== sectionId);
			const next = nextSections[0] || null;
			setSelectedSectionId(next ? next.id : '');
			setSelectedItemId(next && next.items && next.items[0] ? next.items[0].id : '');
			return { ...prev, sections: nextSections };
		});
	};

	const moveSection = (sectionId, direction) => {
		updateDocument(prev => {
			const sections = [...(prev.sections || [])];
			const index = sections.findIndex(x => x.id === sectionId);
			const swapIndex = index + direction;
			if (index < 0 || swapIndex < 0 || swapIndex >= sections.length) return prev;
			[sections[index], sections[swapIndex]] = [sections[swapIndex], sections[index]];
			return { ...prev, sections: sections.map((s, i) => ({ ...s, displayOrder: i + 1 })) };
		});
	};

	const addItem = () => {
		if (!selectedSection) return;
		const item = createItem(selectedSection.id, (selectedSection.items || []).length);
		updateSection(selectedSection.id, s => ({ ...s, items: [...(s.items || []), item] }));
		setSelectedItemId(item.id);
	};

	const addPersonCard = () => {
		if (!selectedSection) return;
		const item = createPersonCard(selectedSection.id, (selectedSection.items || []).length);
		updateSection(selectedSection.id, s => ({ ...s, items: [...(s.items || []), item] }));
		setSelectedItemId(item.id);
	};

	const duplicateItem = () => {
		if (!selectedSection || !selectedItem) return;
		const copy = {
			...selectedItem,
			id: newGuid(),
			title: `${selectedItem.title} (Copy)`,
			displayOrder: (selectedSection.items || []).length + 1,
			people: (selectedItem.people || []).map(p => ({ ...p, id: newGuid() }))
		};
		updateSection(selectedSection.id, s => ({ ...s, items: [...(s.items || []), copy] }));
		setSelectedItemId(copy.id);
	};

	const deleteItem = (itemId) => {
		if (!selectedSection || !window.confirm('Delete this item?')) return;
		updateSection(selectedSection.id, section => {
			const nextItems = (section.items || []).filter(x => x.id !== itemId).map((item, idx) => ({ ...item, displayOrder: idx + 1 }));
			setSelectedItemId(nextItems[0 ? nextItems[0].id : '']);
			return { ...section, items: nextItems };
		});
	};

	const moveItem = (itemId, direction) => {
		if (!selectedSection) return;
		updateSection(selectedSection.id, section => {
			const items = [...(section.items || [])];
			const index = items.findIndex(x => x.id === itemId);
			const swapIndex = index + direction;
			if (index < 0 || swapIndex < 0 || swapIndex >= items.length) return section;
			[items[index], items[swapIndex]] = [items[swapIndex], items[index]];
			return { ...section, items: items.map((item, idx) => ({ ...item, displayOrder: idx + 1 })) };
		});
	};

	const uploadFile = async (event, targetField) => {
		if (!selectedSection || !selectedItem) return;
		const file = event.target.files && event.target.files[0];
		if (!file) return;
		setUploading(true);
		setError('');
		try {
			const result = await handbookService.uploadFile(file);
			updateItem(selectedSection.id, selectedItem.id, item => ({ ...item, [targetField]: result.url }));
		} catch (e) {
			setError('Upload failed.');
		} finally {
			setUploading(false);
		}
	};

	const uploadPdfFile = async (event, pdfId) => {
		if (!selectedSection || !selectedItem) return;
		const file = event.target.files && event.target.files[0];
		if (!file) return;
		setUploading(true);
		setError('');
		try {
			const result = await handbookService.uploadFile(file);
			updatePdf(pdfId, pdf => ({ ...pdf, url: result.url }));
		} catch (e) {
			setError('Upload failed.');
		} finally {
			setUploading(false);
			event.target.value = '';
		}
	};

	// Upload PDF into specified field (url or url_cy)
	const uploadPdfFileFor = async (event, pdfId, targetField) => {
		if (!selectedSection || !selectedItem) return;
		const file = event.target.files && event.target.files[0];
		if (!file) return;
		setUploading(true);
		setError('');
		try {
			const result = await handbookService.uploadFile(file);
			updatePdf(pdfId, pdf => ({ ...pdf, [targetField]: result.url }));
		} catch (e) {
			setError('Upload failed.');
		} finally {
			setUploading(false);
			event.target.value = '';
		}
	};

	const saveAll = async () => {
		setSaving(true);
		setError('');
		setSuccess('');
		const currentSectionId = selectedSectionId;
		const currentItemId = selectedItemId;
		try {
			const latestDocument = documentRef.current || { sections: [] };
			const payload = {
				...latestDocument,
				sections: (latestDocument.sections || []).map(section => ({
					...section,
					items: (section.items || []).map(item => {
						// ensure arrays are present so properties like pdfs/url_cy aren't lost
						const peopleList = item.people || [];
						const linksList = item.links || [];
						const pdfsList = item.pdfs || [];

						if (item.itemType === 'PersonCard') {
							const people = peopleList;
							const first = people[0] || null;
							return {
								...item,
								people,
								content: JSON.stringify({ people }),
								personName: first ? (first.name || '') : '',
								personRole: first ? (first.role || '') : '',
								personRole_cy: first ? (first.role_cy || '') : '',
								personEmail: first ? (first.email || '') : '',
								personPhone: first ? (first.phone || '') : '',
								imageUrl: first ? (first.imageUrl || '') : item.imageUrl
							};
						}

						if (item.itemType === 'Link') {
							const links = linksList.map(l => ({
								...l,
								htmlContent: l.htmlContent || '',
								htmlContent_cy: l.htmlContent_cy || '',
								url_cy: l.url_cy || ''
							}));
							return { ...item, links, content: JSON.stringify({ links }) };
						}

						if (item.itemType === 'Pdf') {
							const pdfs = pdfsList.map(pdf => ({
								...pdf,
								htmlContent: pdf.htmlContent || '',
								htmlContent_cy: pdf.htmlContent_cy || '',
								// preserve url_cy if present, otherwise fall back to url
								url_cy: (typeof pdf.url_cy !== 'undefined' && pdf.url_cy !== null) ? pdf.url_cy : (pdf.url || '')
							}));
							return { ...item, pdfs, content: JSON.stringify({ pdfs }) };
						}

						// ensure arrays are always present for other item types too
						return { ...item, people: peopleList, links: linksList, pdfs: pdfsList };
					})
				}))
			};

			await handbookService.saveAdminDocument(payload);
			setSuccess('Handbook saved successfully!');
			await load(currentSectionId, currentItemId);
		} catch (e) {
			setError('Could not save handbook.');
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <div className="p-4"><Spinner size="sm" /> Loading handbook editor...</div>;

	const sectionCount = (document.sections || []).length;
	const itemCount = (document.sections || []).reduce((sum, s) => sum + (s.items || []).length, 0);

	return (
		<div className="hb-admin">
			{/* Top toolbar */}
			<div className="hb-admin-toolbar">
				<div className="hb-admin-toolbar-left">
					<h4 className="mb-0">Handbook Editor</h4>
					<span className="hb-admin-stats">{sectionCount} section{sectionCount !== 1 ? 's' : ''}, {itemCount} item{itemCount !== 1 ? 's' : ''}</span>
				</div>
				<div className="hb-admin-toolbar-right">
					<Button color="light" size="sm" onClick={() => setShowPreview(!showPreview)} className="mr-2">
						{showPreview ? 'Hide Preview' : 'Show Preview'}
					</Button>
					<Button color="primary" onClick={saveAll} disabled={saving || uploading}>
						{saving ? <><Spinner size="sm" /> Saving...</> : <><FiSave size={14} style={{ marginRight: '4px' }} /> Save Handbook</>}
					</Button>
				</div>
			</div>

			{error && <Alert color="danger" className="mx-3 mt-3 mb-0">{error}</Alert>}
			{success && <Alert color="success" className="mx-3 mt-3 mb-0">{success}</Alert>}

			<div className="hb-admin-layout">
				{/* Left: Section navigator */}
				<div className="hb-admin-nav-panel">
					<div className="hb-admin-nav-header">
						<strong>Sections</strong>
						<ButtonGroup size="sm">
							<Button color="success" size="sm" onClick={() => addSection(null)} title="Add blank section">+ New</Button>
						</ButtonGroup>
					</div>
					<div className="hb-admin-section-list">
						{(document.sections || []).map((section, idx) => (
							<div
								key={section.id}
								className={`hb-admin-section-item ${selectedSection && selectedSection.id === section.id ? 'active' : ''} ${!section.enabled ? 'disabled' : ''}`}
								onClick={() => { setSelectedSectionId(section.id); setSelectedItemId(section.items && section.items[0] ? section.items[0].id : ''); }}
							>
								<div className="hb-admin-section-item-main">
									<div className="hb-admin-section-item-name">{section.name}</div>
									<div className="hb-admin-section-item-meta">
										<Badge color={section.enabled ? 'success' : 'secondary'} className="mr-1" style={{ fontSize: '0.7em' }}>
											{section.enabled ? 'On' : 'Off'}
										</Badge>
										<span>{(section.items || []).length} item{(section.items || []).length !== 1 ? 's' : ''}</span>
									</div>
								</div>
								<div className="hb-admin-section-item-actions" onClick={e => e.stopPropagation()}>
									<button className="hb-admin-btn-mini" onClick={() => moveSection(section.id, -1)} disabled={idx === 0} title="Move up"><FiChevronUp size={12} /></button>
									<button className="hb-admin-btn-mini" onClick={() => moveSection(section.id, 1)} disabled={idx === sectionCount - 1} title="Move down"><FiChevronDown size={12} /></button>
								</div>
							</div>
						))}
						{sectionCount === 0 && (
							<div className="p-3 text-center text-muted" style={{ fontSize: '0.9em' }}>
								No sections yet. Click <strong>+ New</strong> or pick a template to get started.
							</div>
						)}
					</div>
				</div>

				{/* Middle: Item list + editor */}
				<div className="hb-admin-main-panel">
					{selectedSection ? (
						<>
							{/* Section header bar */}
							<div className="hb-admin-section-bar">
								<div className="hb-admin-section-bar-left">
									<h5 className="mb-0">{selectedSection.name}</h5>
									<button className="hb-admin-text-btn" onClick={() => setSectionSettingsOpen(!sectionSettingsOpen)}>
										{sectionSettingsOpen ? 'Hide settings' : 'Section settings'} {sectionSettingsOpen ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
									</button>
								</div>
								<div>
									<Button color="danger" size="sm" outline onClick={() => deleteSection(selectedSection.id)}>Delete Section</Button>
								</div>
							</div>

							<Collapse isOpen={sectionSettingsOpen}>
								<SectionSettingsPanel
									section={selectedSection}
									onChange={handleSectionChange}
								/>
							</Collapse>

							{/* Items area */}
							<div className="hb-admin-items-toolbar">
								<span className="hb-admin-items-count">{(selectedSection.items || []).length} item{(selectedSection.items || []).length !== 1 ? 's' : ''}</span>
								<div>
									<Button color="success" size="sm" onClick={addItem} className="mr-1">+ Add Item</Button>
									{selectedItem && <Button color="light" size="sm" onClick={duplicateItem}>Duplicate</Button>}
								</div>
							</div>

							<div className="hb-admin-items-and-editor">
								{/* Item list */}
								<div className="hb-admin-item-list">
									{(selectedSection.items || []).map((item, idx) => (
										<div
											key={item.id}
											className={`hb-admin-item-row ${selectedItem && selectedItem.id === item.id ? 'active' : ''} ${!item.enabled ? 'disabled' : ''}`}
											onClick={() => setSelectedItemId(item.id)}
										>
											<div className="hb-admin-item-row-main">
												<ItemTypeIcon itemType={item.itemType} />
												<div>
													<div className="hb-admin-item-row-title">
														{item.title || (item.people && item.people.length > 0 ? item.people.map(p => p.name).filter(Boolean).join(', ') : '(no title)')}
													</div>
													<div className="hb-admin-item-row-meta">
														<Badge color="info" style={{ fontSize: '0.7em' }}>{item.itemType}</Badge>
														{item.itemType === 'PersonCard' && item.people && item.people.length > 0 && (
															<span className="ml-1 text-muted" style={{ fontSize: '0.75em' }}>{item.people.length} {item.people.length === 1 ? 'person' : 'people'}</span>
														)}
													</div>
												</div>
											</div>
											<div className="hb-admin-item-row-actions" onClick={e => e.stopPropagation()}>
												<button className="hb-admin-btn-mini" onClick={() => moveItem(item.id, -1)} disabled={idx === 0}><FiChevronUp size={12} /></button>
												<button className="hb-admin-btn-mini" onClick={() => moveItem(item.id, 1)} disabled={idx === (selectedSection.items || []).length - 1}><FiChevronDown size={12} /></button>
												<button className="hb-admin-btn-mini hb-admin-btn-delete" onClick={() => deleteItem(item.id)}><FiX size={12} /></button>
											</div>
										</div>
									))}
									{(selectedSection.items || []).length === 0 && (
										<div className="p-3 text-center text-muted" style={{ fontSize: '0.9em' }}>
											No items yet. Click <strong>+ Add Item</strong> to create content.
										</div>
									)}
								</div>

								{/* Item editor */}
								<div className="hb-admin-item-editor">
									{selectedItem ? (
										<ItemEditorPanel
											item={selectedItem}
											section={selectedSection}
											onItemChange={handleItemChange}
											onUpdateItem={handleUpdateItem}
											onAddPerson={addPerson}
											onDeletePerson={deletePerson}
											onMovePerson={movePerson}
											onUpdatePerson={updatePerson}
											onAddLink={addLink}
											onDeleteLink={deleteLink}
											onMoveLink={moveLink}
											onUpdateLink={updateLink}
											onAddPdf={addPdf}
											onDeletePdf={deletePdf}
											onMovePdf={movePdf}
											onUpdatePdf={updatePdf}
											uploading={uploading}
											uploadPdfFileFor={uploadPdfFileFor}
										/>
									) : (
										<div className="hb-admin-item-empty">
											<div className="hb-admin-item-empty-icon"><FiClipboard size={40} /></div>
											<p>Select an item from the list to edit, or click <strong>+ Add Item</strong> to create new content.</p>
										</div>
									)}
								</div>
							</div>
						</>
					) : (
						<div className="hb-admin-empty-state">
							<div className="hb-admin-empty-icon"><FiBookOpen size={48} /></div>
							<h5>Get started with your handbook</h5>
							<p>Add your first section using the panel on the left, or choose a template to set up common sections quickly.</p>
						</div>
					)}
				</div>

				{/* Right: Live preview */}
				{showPreview && selectedItem && (
					<div className="hb-admin-preview-panel">
						<div className="hb-admin-preview-header">Live Preview</div>
						<div className="hb-admin-preview-body">
							<ItemMiniPreview item={selectedItem} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
